/** Загрузка атласа по манифесту `AtlasManifest` в текстуры PixiJS. */
import type { AtlasManifest, GameMode } from "@newfungame/contracts";
import { Assets, Rectangle, Texture } from "pixi.js";
import { imageUrl, mergeManifests } from "./atlas-merge";

export interface LoadedAtlas {
  manifest: AtlasManifest;
  /** unit — во сколько раз уменьшить кадр, чтобы клетка стала 1 px: 1 / tilePixels его атласа. */
  frames: Map<string, { texture: Texture; anchorX: number; anchorY: number; unit: number }>;
}

async function fetchManifest(base: string, name: string): Promise<AtlasManifest> {
  const res = await fetch(`${base}${name}.json`);
  if (!res.ok) throw new Error(`atlas ${name}: HTTP ${res.status}`);
  // Манифест генерирует наш же конвейер; полная проверка схемой — в тестах tools/voxel, не в рантайме (вес zod-разбора).
  return (await res.json()) as AtlasManifest;
}

/**
 * Загрузить манифесты и картинки. `base` — URL папки атласа (`/assets/atlas/`). Несколько атласов склеиваются
 * ({@link mergeManifests}): более поздний перекрывает анимации раннего — `["placeholder", "voxel"]` рисует готовые
 * воксельные модели, а остальное — заглушкой.
 */
export async function loadAtlas(base: string, ...names: [string, ...string[]]): Promise<LoadedAtlas> {
  const list = await Promise.all(names.map((n) => fetchManifest(base, n)));
  const { manifest, frameTilePixels, imageSource } = mergeManifests(list);
  // кубы-заглушки — пиксель в пиксель; рендер вокселей со сглаженными краями уменьшается плавно
  const images = await Promise.all(
    manifest.images.map((img, i) =>
      Assets.load<Texture>({
        src: imageUrl(base, img),
        data: { scaleMode: list[imageSource[i] ?? 0]?.generator === "voxel" ? "linear" : "nearest" },
      }),
    ),
  );
  const frames: LoadedAtlas["frames"] = new Map();
  for (const [key, f] of Object.entries(manifest.frames)) {
    const source = images[f.image]?.source;
    if (!source) continue;
    frames.set(key, {
      texture: new Texture({ source, frame: new Rectangle(f.x, f.y, f.w, f.h) }),
      anchorX: f.anchor.x / f.w,
      anchorY: f.anchor.y / f.h,
      unit: 1 / (frameTilePixels.get(key) ?? manifest.tilePixels),
    });
  }
  return { manifest, frames };
}

/** Атласы первого кадра: воксели, площадь под полем (ISSUE-87), штаб, турель и платформы. */
export const FIRST_ATLASES = ["voxel", "arena", "chibi-base"] as const;

/** Чиби-персонажи (MADR 0015): каждый атлас перекрывает воксельные анимации своей сущности. Кладутся после `voxel-boss`. */
export const CHIBI_ATLASES = [
  "chibi",
  "chibi-walker",
  "chibi-runner",
  "chibi-bomber",
  "chibi-brute",
  "chibi-sparky",
  "chibi-blaze",
  "chibi-frosty",
  "chibi-shield",
  "chibi-boss-conehead",
  "chibi-boss-butcher",
  "chibi-boss-broodmother",
  "chibi-fx",
] as const;

/** Чиби премиум-героев беты (ISSUE-157): грузятся только в режиме беты, бюджет основной игры не трогают. */
export const BETA_CHIBI_ATLASES = ["chibi-radiation", "chibi-cryo", "chibi-laser", "chibi-plague"] as const;

/** Админ-страницы (эфир, повтор волны) показывают бой любого режима: грузят атласы беты тоже, лишних кадров не жаль. */
export const ADMIN_MODE: GameMode = "beta";

/** Чиби-атласы режима: основные, а в бете ещё и премиум-герои. */
export function chibiAtlases(mode: GameMode): [string, ...string[]] {
  return mode === "beta" ? [...CHIBI_ATLASES, ...BETA_CHIBI_ATLASES] : [...CHIBI_ATLASES];
}
